import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { DATA_SOURCES_COUNT } from './emp';

export type SeoPage = 'main' | 'solutions' | 'news' | 'library' | 'about-us' | 'contact-us';
type SupportedLanguage = 'en' | 'fr' | 'de' | 'nl';

type SeoDefinition = {
  title: string;
  description: string;
  keywords: string;
};

type SeoMetaConfig = {
  path: string;
  image?: string;
  type?: string;
};

const SEO_META: Record<SeoPage, Record<SupportedLanguage, SeoDefinition>> = {
  main: {
    en: {
      title: 'Energy Market Platform | Prices, Hedging & Forecasting | EMP',
      description: `Real-time energy prices, hedging tools, AI forecasting, budget reporting & data APIs - all in one platform. ${DATA_SOURCES_COUNT}+ markets (electricity, gas, oil, CO2), updated every 2 minutes. Built for energy buyers, suppliers, and consultants.`,
      keywords:
        'EMP, EMP energy, EnergyMarketPrice, energy prices real-time, energy spot prices, energy forward prices, wholesale energy data, energy market data, energy prices live, energy price portal, energy management platform'
    },
    fr: {
      title: "Plateforme du marche de l'energie | Prix, Couverture et Previsions | EMP",
      description:
        "Prix de l'energie en temps reel, outils de couverture, previsions IA, reporting budgetaire et API de donnees - tout sur une seule plateforme. 14 000+ marches (electricite, gaz, petrole, CO2), mis a jour toutes les 2 minutes. Concue pour les acheteurs, fournisseurs et consultants en energie.",
      keywords:
        "EMP, EMP energie, EnergyMarketPrice, prix energie temps reel, prix spot energie, prix forward energie, donnees energie de gros, donnees marche energie, prix energie en direct, portail prix energie, plateforme de gestion de l'energie"
    },
    de: {
      title: 'Energieplattform | Preise, Hedging und Prognosen | EMP',
      description: `Energiepreise in Echtzeit, Hedging-Tools, KI-Prognosen, Budget-Reporting und Daten-APIs - alles in einer Plattform. ${DATA_SOURCES_COUNT}+ Maerkte (Strom, Gas, Oel, CO2), alle 2 Minuten aktualisiert. Entwickelt fuer Energieeinkaeufer, Lieferanten und Berater.`,
      keywords:
        'EMP, EMP energie, EnergyMarketPrice, energiepreise echtzeit, energie spotpreise, energie terminpreise, grosshandels-energiedaten, energiemarktdaten, energiepreise live, energiepreis portal, energiemanagement plattform'
    },
    nl: {
      title: 'Energie Marktplatform | Prijzen, Hedging en Voorspellingen | EMP',
      description: `Realtime energieprijzen, hedgingtools, AI-voorspellingen, budgetrapportage en data-APIs - alles in een platform. ${DATA_SOURCES_COUNT}+ markten (elektriciteit, gas, olie, CO2), elke 2 minuten geupdate. Gebouwd voor energie-inkopers, leveranciers en consultants.`,
      keywords:
        'EMP, EMP energie, EnergyMarketPrice, energieprijzen realtime, energie spotprijzen, energie termijnprijzen, groothandel energiedata, energiemarktdata, energieprijzen live, energie prijsportaal, energiebeheer platform'
    }
  },
  solutions: {
    en: {
      title: 'Energy Management Solutions | Data, Hedging, Forecasting & Reporting | EMP',
      description:
        '8 SaaS tools for energy suppliers, consultants & corporate buyers. AI price forecasting, flex contract management, budget reporting by site, real-time market data and AI-powered news monitoring. Go live in 24 hours. More intuitive than legacy platforms.',
      keywords:
        'EMP energy solutions, energy procurement software B2B, energy management platform, energy cost optimization SaaS, energy market analysis platform, energy SaaS solutions, energy retailer software'
    },
    fr: {
      title: "Solutions de gestion de l'energie | Donnees, Couverture, Previsions et Reporting | EMP",
      description:
        "8 outils SaaS pour fournisseurs d'energie, consultants et acheteurs corporate. Previsions IA des prix, gestion des contrats flex, reporting budgetaire par site, donnees de marche en temps reel et veille actualites par IA. Mise en service en 24 heures. Plus intuitif que les plateformes legacy.",
      keywords:
        'solutions energie EMP, logiciel achat energie B2B, plateforme gestion energie, SaaS optimisation cout energie, plateforme analyse marche energie, solutions SaaS energie, logiciel fournisseur energie'
    },
    de: {
      title: 'Energiemanagement-Loesungen | Daten, Hedging, Prognosen und Reporting | EMP',
      description:
        '8 SaaS-Tools fuer Energieversorger, Berater und Unternehmenskunden. KI-Preisprognosen, Flexvertrags-Management, standortbezogenes Budget-Reporting, Echtzeit-Marktdaten und KI-gestuetztes News-Monitoring. In 24 Stunden live. Intuitiver als Legacy-Plattformen.',
      keywords:
        'EMP energieloesungen, energieeinkauf software B2B, energiemanagement plattform, SaaS energiekosten optimierung, energiemarkt analyse plattform, energie SaaS loesungen, software energieversorger'
    },
    nl: {
      title: 'Energiemanagement Oplossingen | Data, Hedging, Voorspelling en Rapportage | EMP',
      description:
        '8 SaaS-tools voor energieleveranciers, consultants en zakelijke inkopers. AI-prijsvoorspelling, flexcontractbeheer, budgetrapportage per locatie, realtime marktdata en AI-gestuurde nieuwsmonitoring. Live in 24 uur. Intuitiever dan legacy-platforms.',
      keywords:
        'EMP energieoplossingen, energie inkoopsoftware B2B, energiebeheer platform, SaaS energiekosten optimalisatie, energiemarkt analyse platform, energie SaaS oplossingen, software energieleverancier'
    }
  },
  news: {
    en: {
      title: 'European Energy Market News | Daily Prices & Sector Updates',
      description:
        'Stay informed with daily energy market news. Expert analysis on green hydrogen, energy investments, pipelines, and electricity price trends across Europe.',
      keywords:
        'energy market news, European energy news, power market news, energy investment updates, energy sector analysis'
    },
    fr: {
      title: "Actualites du marche europeen de l'energie | Prix quotidiens et mises a jour secteur",
      description:
        "Restez informe avec les actualites quotidiennes du marche de l'energie. Analyses expertes sur l'hydrogene vert, les investissements energie, les pipelines et les tendances de prix de l'electricite en Europe.",
      keywords:
        'actualites marche energie, actualites energie europe, actualites marche electrique, mises a jour investissements energie, analyse secteur energie'
    },
    de: {
      title: 'Europaeische Energiemarkt-News | Taegliche Preise und Branchen-Updates',
      description:
        'Bleiben Sie mit taeglichen Energiemarkt-News informiert. Expertenanalysen zu gruener Wasserstoff, Energieinvestitionen, Pipelines und Strompreistrends in Europa.',
      keywords:
        'energiemarkt news, europa energie news, strommarkt news, energieinvestitionen updates, energiesektor analyse'
    },
    nl: {
      title: 'Europees Energiemarktnieuws | Dagelijkse prijzen en sectorupdates',
      description:
        'Blijf op de hoogte met dagelijks energiemarktnieuws. Expertanalyse over groene waterstof, energie-investeringen, pijpleidingen en elektriciteitsprijs-trends in Europa.',
      keywords:
        'energiemarktnieuws, europees energienieuws, stroommarktnieuws, updates energie-investeringen, analyse energiesector'
    }
  },
  library: {
    en: {
      title: 'Energy Intelligence Library | Market Guides, Hedging Strategies & Best Practices',
      description:
        'Expert insights on energy hedging strategies, price volatility management, procurement best practices and market optimisation. Guides for energy buyers, suppliers and consultants.',
      keywords:
        'energy hedging strategy guide, energy market report, energy price prediction insights, energy procurement best practices, energy risk management guide'
    },
    fr: {
      title: "Bibliotheque d'intelligence energie | Guides de marche, strategies de couverture et bonnes pratiques",
      description:
        "Analyses expertes sur les strategies de couverture energie, la gestion de la volatilite des prix, les bonnes pratiques d'achat et l'optimisation de marche. Guides pour acheteurs, fournisseurs et consultants en energie.",
      keywords:
        'guide strategie couverture energie, rapport marche energie, insights prediction prix energie, bonnes pratiques achat energie, guide gestion risque energie'
    },
    de: {
      title: 'Energy Intelligence Bibliothek | Marktleitfaeden, Hedging-Strategien und Best Practices',
      description:
        'Experteneinblicke zu Energie-Hedging-Strategien, Preisvolatilitaets-Management, Beschaffungs-Best-Practices und Marktoptimierung. Leitfaeden fuer Energieeinkaeufer, Lieferanten und Berater.',
      keywords:
        'energie hedging strategie leitfaden, energiemarkt bericht, energiepreis prognose einblicke, energieeinkauf best practices, energie risikomanagement leitfaden'
    },
    nl: {
      title: 'Energy Intelligence Bibliotheek | Marktgidsen, hedgingstrategieen en best practices',
      description:
        'Expertinzichten over energie-hedgingstrategieen, beheer van prijsvolatiliteit, inkoop best practices en marktoptimalisatie. Gidsen voor energie-inkopers, leveranciers en consultants.',
      keywords:
        'energie hedgingstrategie gids, energiemarktrapport, inzichten energieprijs voorspelling, energie inkoop best practices, energie risicobeheer gids'
    }
  },
  'about-us': {
    en: {
      title: 'About Energy Market Price (EMP) | Energy Market Intelligence Platform',
      description: `EMP delivers energy market intelligence to suppliers, consultants and corporate buyers across 20+ countries. ${DATA_SOURCES_COUNT}+ markets, 5 million data points, 8 SaaS solutions. One platform for prices, hedging, forecasting and reporting.`,
      keywords:
        'EMP, EMP energy, EnergyMarketPrice, Energy Market Price company, Market Intelligence Group SA, energy data provider, energy market expertise, energy SaaS provider'
    },
    fr: {
      title: 'A propos de Energy Market Price (EMP) | Plateforme intelligence marche energie',
      description:
        "EMP fournit de l'intelligence de marche energie aux fournisseurs, consultants et acheteurs corporate dans plus de 20 pays. 14 000+ marches, 5 millions de points de donnees, 8 solutions SaaS. Une seule plateforme pour prix, couverture, previsions et reporting.",
      keywords:
        'EMP, EMP energie, EnergyMarketPrice, societe Energy Market Price, Market Intelligence Group SA, fournisseur donnees energie, expertise marche energie, fournisseur SaaS energie'
    },
    de: {
      title: 'Ueber Energy Market Price (EMP) | Plattform fuer Energiemarkt-Intelligence',
      description: `EMP liefert Energiemarkt-Intelligence fuer Lieferanten, Berater und Unternehmenskunden in ueber 20 Laendern. ${DATA_SOURCES_COUNT}+ Maerkte, 5 Millionen Datenpunkte, 8 SaaS-Loesungen. Eine Plattform fuer Preise, Hedging, Prognosen und Reporting.`,
      keywords:
        'EMP, EMP energie, EnergyMarketPrice, Energy Market Price unternehmen, Market Intelligence Group SA, energie datenanbieter, energiemarkt expertise, energie SaaS anbieter'
    },
    nl: {
      title: 'Over Energy Market Price (EMP) | Platform voor energiemarkt-intelligence',
      description: `EMP levert energiemarkt-intelligence aan leveranciers, consultants en zakelijke inkopers in 20+ landen. ${DATA_SOURCES_COUNT}+ markten, 5 miljoen datapunten, 8 SaaS-oplossingen. Een platform voor prijzen, hedging, voorspelling en rapportage.`,
      keywords:
        'EMP, EMP energie, EnergyMarketPrice, Energy Market Price bedrijf, Market Intelligence Group SA, energie data provider, energiemarkt expertise, energie SaaS provider'
    }
  },
  'contact-us': {
    en: {
      title: 'Contact Energy Market Price | Request a Free Demo',
      description:
        'Get a personalised demo of our energy solutions - go live in 24 hours. Talk to our experts about data, hedging, forecasting, reporting or API integration.',
      keywords:
        'contact EMP, contact energy market price, energy software demo, energy data inquiry, energy platform demo request'
    },
    fr: {
      title: 'Contactez Energy Market Price | Demandez une demo gratuite',
      description:
        "Obtenez une demo personnalisee de nos solutions energie - mise en service en 24 heures. Echangez avec nos experts sur les donnees, la couverture, les previsions, le reporting ou l'integration API.",
      keywords:
        'contacter EMP, contacter energy market price, demo logiciel energie, demande donnees energie, demande demo plateforme energie'
    },
    de: {
      title: 'Kontakt Energy Market Price | Kostenlose Demo anfragen',
      description:
        'Erhalten Sie eine personalisierte Demo unserer Energieloesungen - in 24 Stunden live. Sprechen Sie mit unseren Experten ueber Daten, Hedging, Prognosen, Reporting oder API-Integration.',
      keywords:
        'kontakt EMP, kontakt energy market price, energie software demo, energie daten anfrage, energie plattform demo anfrage'
    },
    nl: {
      title: 'Contact Energy Market Price | Vraag een gratis demo aan',
      description:
        'Krijg een gepersonaliseerde demo van onze energieoplossingen - live in 24 uur. Praat met onze experts over data, hedging, voorspelling, rapportage of API-integratie.',
      keywords:
        'contact EMP, contact energy market price, demo energiesoftware, aanvraag energiedata, demo aanvraag energieplatform'
    }
  }
};

function getLanguage(localeId: string): SupportedLanguage {
  const lang = localeId.split('-')[0]?.toLowerCase();
  if (lang === 'fr' || lang === 'de' || lang === 'nl') {
    return lang;
  }
  return 'en';
}

export function applySeoMeta(page: SeoPage, localeId: string, titleService: Title, metaService: Meta): void {
  const lang = getLanguage(localeId);
  const seo = SEO_META[page][lang];
  const config = getSeoMetaConfig(page, lang);

  titleService.setTitle(seo.title);
  metaService.updateTag({ name: 'description', content: seo.description });
  metaService.updateTag({ name: 'keywords', content: seo.keywords });
  metaService.updateTag({ property: 'og:title', content: seo.title });
  metaService.updateTag({ property: 'og:description', content: seo.description });
  metaService.updateTag({ property: 'og:url', content: config.path });
  metaService.updateTag({ property: 'og:image', content: config.image });
  metaService.updateTag({ property: 'og:type', content: config.type });
  metaService.updateTag({ property: 'twitter:title', content: seo.title });
  metaService.updateTag({ property: 'twitter:description', content: seo.description });
  metaService.updateTag({ property: 'twitter:image', content: config.image });
  metaService.updateTag({ property: 'twitter:image:src', content: config.image });
}

function getSeoMetaConfig(page: SeoPage, lang: SupportedLanguage): Required<SeoMetaConfig> {
  const baseUrl = `${environment.url}${environment.basehref}/${lang}`;
  const defaultImage = `${environment.url}${environment.basehref}/${lang}/assets/img/Backgr-BannerArtboard.png`;

  switch (page) {
    case 'main':
      return { path: baseUrl, image: defaultImage, type: 'website' };
    case 'solutions':
      return { path: `${baseUrl}/solutions`, image: defaultImage, type: 'website' };
    case 'news':
      return {
        path: `${baseUrl}/news`,
        image: `${environment.url}${environment.basehref}/${lang}/assets/img/daily-market-update.webp`,
        type: 'website'
      };
    case 'library':
      return { path: `${baseUrl}/library`, image: defaultImage, type: 'website' };
    case 'about-us':
      return { path: `${baseUrl}/about-us`, image: defaultImage, type: 'website' };
    case 'contact-us':
      return { path: `${baseUrl}/contact-us`, image: defaultImage, type: 'website' };
  }
}
